import React, { Fragment, useState } from 'react';
import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/autoplay';
import 'swiper/css/keyboard';
import 'swiper/css/navigation';
import { A11y, Autoplay, Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import ClientSideSlideshowLightbox from '../ClientSideSlideshowLightbox';
import { Picto } from '../Picto';

interface ImagesCarouselProps {
  imageUrls: string[];
  swiperProps?: SwiperProps;
}

const ImagesCarousel: React.FC<ImagesCarouselProps> = ({
  imageUrls,
  swiperProps,
}: ImagesCarouselProps) => {
  const [swiper, setSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(-1);

  if (!imageUrls?.length) return null;

  const hasMultiple = imageUrls.length > 1;

  return (
    <div className="ImagesCarousel">
      <div className="ImagesCarousel__carousel">
        <div className="ImagesCarousel__media">
          <Swiper
            modules={[
              Keyboard,
              Navigation,
              A11y,
              swiperProps?.autoplay ? Autoplay : null,
              ...(swiperProps?.modules || []),
            ]}
            loop={hasMultiple}
            {...swiperProps}
            onSwiper={(s) => {
              setSwiper(s);
              swiperProps?.onSwiper?.(s);
            }}
            onSlideChange={(s) => {
              setActiveIndex(s.realIndex);
              swiperProps?.onSlideChange?.(s);
            }}
          >
            {imageUrls.map((imageUrl, index) => (
              <SwiperSlide key={index}>
                <img src={imageUrl} alt={`${index}`} onClick={() => setLightboxImageIndex(index)} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        {hasMultiple ? (
          <Fragment>
            <div className="ImagesCarousel__carousel__pagination">
              {imageUrls.map((_, index) => (
                <button
                  type="button"
                  key={index}
                  aria-label={`Photo ${index + 1}`}
                  className={`ImagesCarousel__carousel__pagination__button ${
                    activeIndex === index
                      ? 'ImagesCarousel__carousel__pagination__button--active'
                      : ''
                  }`}
                  onClick={() => {
                    if (!swiper) return;
                    const loopEnabled = (swiper as { params?: { loop?: boolean } }).params?.loop;
                    if (loopEnabled) {
                      (swiper as { slideToLoop: (i: number) => void }).slideToLoop(index);
                    } else {
                      (swiper as { slideTo: (i: number) => void }).slideTo(index);
                    }
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              className="ImagesCarousel__carousel__navigationButton ImagesCarousel__carousel__navigationPrev"
              onClick={() => swiper?.slidePrev()}
            >
              <Picto iconKey="arrowLeftV2" />
            </button>
            <button
              type="button"
              className="ImagesCarousel__carousel__navigationButton ImagesCarousel__carousel__navigationNext"
              onClick={() => swiper?.slideNext()}
            >
              <Picto iconKey="arrowRightV2" />
            </button>
          </Fragment>
        ) : null}
      </div>
      <ClientSideSlideshowLightbox
        images={imageUrls?.map((imageUrl, i) => ({ src: imageUrl, alt: i.toString() }))}
        startingSlideIndex={lightboxImageIndex}
        open={lightboxImageIndex > -1}
        onClose={() => setLightboxImageIndex(-1)}
      />
    </div>
  );
};

export default ImagesCarousel;
